/**
 * Labstep
 *
 * @module components/Layout/GlobalSearch
 * @desc Component to do global search
 */

import classNames from 'classnames';
import { withAuthenticatedUserEntity } from 'labstep-web/containers/AuthenticatedUser';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { MediaQuery } from 'labstep-web/core/MediaQuery';
import { Size } from 'labstep-web/core/MediaQuery/types';
import type { GlobalSearchResult } from 'labstep-web/models/global-search-result.model';
import type { PartialEntity } from 'labstep-web/models/partial-entity.model';
import { useLazySearchQuery } from 'labstep-web/rtk/services/search';
import { navigation, routing } from 'labstep-web/services/navigation';
import { snakeCase } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Input } from 'semantic-ui-react';
import LayoutNewGlobalSearchDocument from './Document';
import styles from './styles.module.scss';
import type { LayoutNewGlobalSearchProps } from './types';

export const LayoutNewGlobalSearch: React.FC<
  LayoutNewGlobalSearchProps
> = ({ authenticatedUserEntity }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const history = useHistory();

  const [triggerSearch, { data: results = [], isLoading, error }] =
    useLazySearchQuery();

  // Scroll selected item into view
  useEffect(() => {
    if (resultItemsRef.current[selectedIndex]) {
      resultItemsRef.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  if (!authenticatedUserEntity.hasGroupNotDeleted) {
    return null;
  }

  const handleResultClick = useCallback(
    (result: GlobalSearchResult) => {
      let target: GlobalSearchResult | PartialEntity = result;
      if (result.parent) {
        target = result.parent;
      }
      const route = `${snakeCase(target.entity_class)}_show`;
      if (route in routing) {
        history.push(
          navigation.get(route as keyof typeof routing, {
            id: target.id,
          }),
        );
        setIsFocused(false);
        setSearchQuery('');
      }
    },
    [history],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      setSelectedIndex(0);

      if (!query || query.trim().length === 0) {
        return;
      }

      if (query.trim().length < 2) {
        return;
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        triggerSearch(query.trim());
      }, 300);
    },
    [triggerSearch],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setTimeout(() => {
      const input = document.querySelector('#global-search input');
      if (input && input instanceof HTMLElement) {
        input.focus();
      }
    }, 0);
  }, []);

  const handleBlur = useCallback(() => {
    // Add a small delay to allow click events to be processed
    setTimeout(() => {
      setIsFocused(false);
    }, 150);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (results.length === 0) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          Math.min(prev + 1, results.length - 1),
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
      }
    },
    [results, selectedIndex, handleResultClick],
  );

  return (
    <MediaQuery size={Size.tablet_desktop}>
      <Flex
        id="global-search"
        className={classNames(styles.globalSearchContainer, {
          [styles.collapsed]: !isFocused,
          [styles.expanded]: isFocused,
        })}
      >
        <div
          className={classNames(styles.searchButton, {
            [styles.hidden]: isFocused,
            [styles.visible]: !isFocused,
          })}
        >
          <ActionComponent
            type="text"
            icon="search"
            text="Global Search"
            onClick={handleFocus}
          />
        </div>
        <div
          className={classNames(styles.searchInputContainer, {
            [styles.hidden]: !isFocused,
            [styles.visible]: isFocused,
          })}
        >
          <div className={styles.inputWrapper}>
            <Input
              size="huge"
              fluid
              icon="search"
              iconPosition="left"
              loading={isLoading}
              placeholder="Search anything..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
            {searchQuery.trim().length >= 2 && (
              <div className={styles.resultsContainer}>
                {isLoading ? (
                  <div className={styles.loadingState}>
                    Loading...
                  </div>
                ) : results.length > 0 ? (
                  results.map((result, index) => (
                    <div
                      key={result.guid}
                      ref={(el) => {
                        resultItemsRef.current[index] = el;
                      }}
                    >
                      <LayoutNewGlobalSearchDocument
                        result={result}
                        onClick={() => handleResultClick(result)}
                        isSelected={index === selectedIndex}
                      />
                    </div>
                  ))
                ) : (
                  <div className={styles.noResults}>
                    {searchQuery.trim().length >= 2 && !isLoading
                      ? `No results found for "${searchQuery}"`
                      : 'Start typing to search'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Flex>
    </MediaQuery>
  );
};

export default withAuthenticatedUserEntity(LayoutNewGlobalSearch);
